import { NgForOf } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
    NgLabelTemplateDirective,
    NgOptionComponent,
    NgOptionTemplateDirective,
    NgSelectComponent,
} from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import {
    Hierarchical,
    HierarchicalAlternative,
} from '@shared/models/question-types/hierarchical';
import { ValidatorComponent } from '../../validator/validator.component';
import { QuestionBaseComponent } from '../question-base/question-base.component';

@Component({
  selector: 'app-hierarchical',
  imports: [
    NgSelectComponent,
    NgOptionComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    ReactiveFormsModule,
    TranslateModule,
    QuestionBaseComponent,
    ValidatorComponent,
    NgForOf,
  ],
  templateUrl: './hierarchical.component.html',
  styleUrl: './hierarchical.component.scss',
})
export class HierarchicalComponent implements AfterViewInit {
  @Input({ required: true }) question!: Hierarchical;
  @Input() formGroup?: any;
  @Input() isFirstQuestion: boolean = false;

  isDropdownOpen = false;
  selectedAlternatives: (HierarchicalAlternative | null)[] = [];
  alternativesForLevels: HierarchicalAlternative[][] = [];
  levelVisibility: boolean[] = [];
  hasFormGroupErrorsFlag = false;
  hasFormGroupBeenTouchedFlag = false;
  validatorType = ValidatorType;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.initializeSelectedAlternatives();
    this.checkControllersValues();
    this.computeAlternativesForLevels();
    this.computeLevelVisibility();
    this.computeFormGroupFlags();
    this.cdr.detectChanges();
  }

  initializeSelectedAlternatives() {
    this.selectedAlternatives = this.question.levels.map((level, i) => {
      const control = this.formGroup.get(`${this.question.id}_${level.id}`);
      return control?.value || null;
    });
  }

  checkControllersValues() {
    for (let i = 0; i < this.question.levels.length; i++) {
      const control = this.formGroup.get(
        `${this.question.id}_${this.question.levels[i].id}`,
      );
      if (control?.value) {
        this.updateNextLevelsState(i);
        break;
      }
    }
  }

  handleOnChange(value: HierarchicalAlternative | null, levelIndex: number) {
    const selectedAlternative = value
      ? this.question.alternatives.find((i) => i.id === value.id) || null
      : null;
    this.selectedAlternatives[levelIndex] = selectedAlternative;

    this.resetControllers(levelIndex);
    this.selectedAlternatives = this.question.levels.map((_, index) =>
      index <= levelIndex ? this.selectedAlternatives[index] || null : null,
    );

    this.updateNextLevelsState(levelIndex);
    this.computeAlternativesForLevels();
    this.computeLevelVisibility();
  }

  resetControllers(levelIndex: number) {
    for (let i = levelIndex + 1; i < this.question.levels.length; i++) {
      const control = this.formGroup.get(
        `${this.question.id}_${this.question.levels[i].id}`,
      );
      control?.reset();
    }
  }

  computeFormGroupFlags() {
    this.hasFormGroupBeenTouchedFlag = Object.keys(
      this.formGroup.controls,
    ).every((key) => this.formGroup.controls[key].touched);
    this.hasFormGroupErrorsFlag = Object.keys(this.formGroup.controls).some(
      (key) => this.formGroup.controls[key].errors !== null,
    );
  }

  computeAlternativesForLevels() {
    this.alternativesForLevels = this.question.levels.map((level, index) => {
      const parentId =
        index === 0 ? null : this.selectedAlternatives[index - 1]?.id;
      return this.getAlternativesForLevel(level.id, parentId);
    });
  }

  getAlternativesForLevel(
    levelId: string,
    parentId: string | null = null,
  ): HierarchicalAlternative[] {
    return this.question.alternatives.filter((alternative) =>
      parentId === null
        ? alternative.levelId === levelId
        : alternative.parentId === parentId && alternative.levelId === levelId,
    );
  }

  hasAlternativesForNextLevel(levelIndex: number): boolean {
    const nextLevel = this.question.levels[levelIndex + 1];
    if (!nextLevel) return false;
    const parentId = this.selectedAlternatives[levelIndex]?.id || null;
    return this.getAlternativesForLevel(nextLevel.id, parentId).length > 0;
  }

  computeLevelVisibility() {
    this.levelVisibility = this.question.levels.map((_, index) => {
      return (
        index === 0 ||
        (!!this.selectedAlternatives[index - 1] &&
          this.hasAlternativesForNextLevel(index - 1))
      );
    });
  }

  updateNextLevelsState(levelIndex: number) {
    let shouldDisableAll = false;
    for (let i = levelIndex + 1; i < this.question.levels.length; i++) {
      const control = this.formGroup.get(
        `${this.question.id}_${this.question.levels[i].id}`,
      );
      const parentId = this.selectedAlternatives[i - 1]?.id || null;
      if (
        shouldDisableAll ||
        this.getAlternativesForLevel(this.question.levels[i].id, parentId)
          .length === 0
      ) {
        control?.disable();
        shouldDisableAll = true;
      } else {
        control?.enable();
      }
    }
    this.cdr.detectChanges();
  }

  getVisibleAlternatives(levelIndex: number) {
    if (
      !this.alternativesForLevels ||
      !this.alternativesForLevels[levelIndex]
    ) {
      return [];
    }

    const alternatives = this.alternativesForLevels[levelIndex];
    return alternatives.filter((alt) => !alt.isHidden);
  }

  getSelectedTitle(levelIndex: number): string {
    const selectedAlternative = this.selectedAlternatives[levelIndex];
    return selectedAlternative?.title ?? '';
  }
}
